import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import { submitBugReport } from "../utils/api/api";
import { APP_VERSION } from "../constants/appVersion";
import { buildManualReportTechnicalFields } from "../utils/support/buildSupportErrorReport";
import { getLastReportableError } from "../utils/support/sanitizeErrorForReport";
import { installClientDiagnosticsBuffer } from "../utils/support/clientDiagnosticsBuffer";
import { resolveClubId } from "../utils/clubScope";
import { getMm } from "../theme/modernMaterialEnterprise";

const OPEN_EVENT = "newera:open-bug-report";

const CATEGORY_KEYS = ["broken", "data", "payment", "access", "other"];

const fieldSx = (isDarkMode) => ({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: getMm(isDarkMode).surfaceLowest,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    "& fieldset": {
      borderColor: getMm(isDarkMode).outlineVariant,
      borderWidth: "2px",
    },
    "&.Mui-focused fieldset": {
      borderColor: getMm(isDarkMode).onSurface,
    },
    "& input, & textarea": {
      color: getMm(isDarkMode).onSurface,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    },
  },
  "& .MuiInputLabel-root": {
    color: getMm(isDarkMode).onSurfaceVariant,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
});

export function openBugReportDialog(prefill) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: prefill || null }));
}

export default function BugReportHost({ selectedPage, userInfo }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const { dialogFormPrimarySx, dialogFormCancelSx } = useSystemButtonStyles();
  const clubID = resolveClubId(userInfo);

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("broken");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successRef, setSuccessRef] = useState("");

  const categoryOptions = useMemo(
    () =>
      CATEGORY_KEYS.map((key) => ({
        value: key,
        label: t(`bug_report_category_${key}`),
      })),
    [t]
  );

  const resetForm = useCallback(() => {
    setCategory("broken");
    setDescription("");
    setSteps("");
    setIncludeDiagnostics(true);
    setCompanyWebsite("");
    setError("");
    setSuccessRef("");
  }, []);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetForm();
  };

  useEffect(() => {
    installClientDiagnosticsBuffer();
  }, []);

  useEffect(() => {
    const onOpen = (event) => {
      const prefill = event?.detail;
      resetForm();
      if (prefill && typeof prefill === "object") {
        if (prefill.category) setCategory(prefill.category);
        if (prefill.description) setDescription(String(prefill.description));
        if (prefill.steps) setSteps(String(prefill.steps));
        if (typeof prefill.includeDiagnostics === "boolean") {
          setIncludeDiagnostics(prefill.includeDiagnostics);
        }
      }
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [resetForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessRef("");

    const trimmed = description.trim();
    if (trimmed.length < 10) {
      setError(t("bug_report_error_description_short"));
      return;
    }

    setSubmitting(true);
    try {
      const technical = buildManualReportTechnicalFields(
        getLastReportableError(),
        includeDiagnostics
      );
      const result = await submitBugReport(
        {
          category,
          description: trimmed,
          steps: steps.trim(),
          page: selectedPage || "",
          urlPath: window.location.pathname + window.location.search,
          includeDiagnostics,
          appVersion: APP_VERSION,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          companyWebsite,
          ...technical,
        },
        clubID
      );
      setSuccessRef(result.referenceId || "");
      setDescription("");
      setSteps("");
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.response?.data?.error || err?.message || t("bug_report_error_generic");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            m: isMobile ? 0 : 2,
            maxHeight: isMobile ? "100%" : "calc(100% - 32px)",
            height: isMobile ? "100%" : "auto",
            display: "flex",
            flexDirection: "column",
            borderRadius: isMobile ? 0 : "8px",
            boxShadow: "none",
            border: isMobile
              ? "none"
              : `1px solid ${getMm(isDarkMode).outlineVariant}`,
            backgroundColor: getMm(isDarkMode).surfaceLowest,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexShrink: 0,
            fontWeight: 700,
            fontSize: { xs: "1.0625rem", sm: "1.125rem" },
            color: getMm(isDarkMode).onSurface,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            pt: {
              xs: "calc(12px + env(safe-area-inset-top, 0px))",
              sm: 2,
            },
            borderBottom: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <BugReportOutlinedIcon sx={{ fontSize: 22, flexShrink: 0 }} />
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: "inherit",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                lineHeight: 1.3,
              }}
            >
              {t("bug_report_title")}
            </Typography>
          </Box>
          <IconButton
            aria-label={t("close")}
            onClick={handleClose}
            disabled={submitting}
            edge="end"
            sx={{
              color: getMm(isDarkMode).onSurfaceVariant,
              flexShrink: 0,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <DialogContent
            sx={{
              flex: 1,
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              pt: { xs: 2, sm: 1 },
              px: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 2 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            {successRef ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  py: { xs: 4, sm: 6 },
                  px: 1,
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{
                    fontSize: 56,
                    color: "#22C55E",
                    mb: 2,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.25rem", sm: "1.375rem" },
                    color: getMm(isDarkMode).onSurface,
                    mb: 1.5,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {t("bug_report_success_title")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.9375rem", sm: "1rem" },
                    color: getMm(isDarkMode).onSurfaceVariant,
                    maxWidth: 360,
                    lineHeight: 1.55,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {t("bug_report_success_message")}
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "0.75rem",
                    color: getMm(isDarkMode).onSurfaceVariant,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {t("bug_report_success_reference", { referenceId: successRef })}
                </Typography>
              </Box>
            ) : (
              <>
            <Typography
              sx={{
                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                color: getMm(isDarkMode).onSurfaceVariant,
                mb: 2,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                lineHeight: 1.5,
              }}
            >
              {t("bug_report_intro")}
            </Typography>

            {error ? (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                {error}
              </Alert>
            ) : null}

            <Box
              sx={{
                position: "absolute",
                left: -9999,
                width: 1,
                height: 1,
                overflow: "hidden",
              }}
              aria-hidden
            >
              <TextField
                tabIndex={-1}
                autoComplete="off"
                name="companyWebsite"
                value={companyWebsite}
                onChange={(ev) => setCompanyWebsite(ev.target.value)}
              />
            </Box>

            <FormControl
              fullWidth
              size={isMobile ? "medium" : "small"}
              sx={{ mb: 2, ...fieldSx(isDarkMode) }}
            >
              <InputLabel id="bug-report-category-label">{t("bug_report_category_label")}</InputLabel>
              <Select
                labelId="bug-report-category-label"
                value={category}
                label={t("bug_report_category_label")}
                onChange={(ev) => setCategory(ev.target.value)}
                disabled={submitting || Boolean(successRef)}
              >
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t("bug_report_description_label")}
              value={description}
              onChange={(ev) => setDescription(ev.target.value)}
              fullWidth
              required
              multiline
              minRows={isMobile ? 6 : 4}
              disabled={submitting || Boolean(successRef)}
              size={isMobile ? "medium" : "small"}
              sx={{ mb: 2, ...fieldSx(isDarkMode) }}
            />

            <TextField
              label={t("bug_report_steps_label")}
              value={steps}
              onChange={(ev) => setSteps(ev.target.value)}
              fullWidth
              multiline
              minRows={isMobile ? 3 : 2}
              disabled={submitting || Boolean(successRef)}
              size={isMobile ? "medium" : "small"}
              sx={{ mb: 1, ...fieldSx(isDarkMode) }}
            />

            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0, mt: 0.5 }}
              control={
                <Checkbox
                  checked={includeDiagnostics}
                  onChange={(ev) => setIncludeDiagnostics(ev.target.checked)}
                  disabled={submitting || Boolean(successRef)}
                  sx={{
                    pt: 0.25,
                    color: getMm(isDarkMode).onSurfaceVariant,
                    "&.Mui-checked": { color: getMm(isDarkMode).onSurface },
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: "0.875rem", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {t("bug_report_diagnostics_label")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: getMm(isDarkMode).onSurfaceVariant,
                      mt: 0.25,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      lineHeight: 1.4,
                    }}
                  >
                    {t("bug_report_diagnostics_hint")}
                  </Typography>
                </Box>
              }
            />
              </>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              flexShrink: 0,
              flexDirection: successRef
                ? "column"
                : { xs: "column-reverse", sm: "row" },
              alignItems: successRef ? "center" : "stretch",
              justifyContent: successRef ? "center" : undefined,
              gap: 1,
              px: { xs: 2, sm: 3 },
              pt: 2,
              pb: {
                xs: "calc(16px + env(safe-area-inset-bottom, 0px))",
                sm: 3,
              },
              borderTop: `1px solid ${getMm(isDarkMode).outlineVariant}`,
              bgcolor: getMm(isDarkMode).surfaceLow,
            }}
          >
            {successRef ? (
              <Button
                type="button"
                variant="contained"
                onClick={handleClose}
                sx={{
                  ...dialogFormPrimarySx,
                  m: 0,
                  width: "100%",
                  maxWidth: 280,
                }}
              >
                {t("close")}
              </Button>
            ) : (
              <>
            <Button
              type="button"
              variant="outlined"
              onClick={handleClose}
              disabled={submitting}
              fullWidth={isMobile}
              sx={{ ...dialogFormCancelSx, m: 0 }}
            >
              {t("cancel")}
            </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                fullWidth={isMobile}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                sx={{ ...dialogFormPrimarySx, m: 0 }}
              >
                {submitting ? t("bug_report_sending") : t("bug_report_submit")}
              </Button>
              </>
            )}
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}
